import styled from "@emotion/styled/macro";
import {
  MOBILE_BREAKPOINT,
  Modal,
  TABLET_BREAKPOINT,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import {
  ReviewOffer,
  ReviewOfferProduct,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  currentUsageCardData,
  maxPowerCardData,
  productionCardData,
  storageCapacityCardData,
  systemSizeCardData,
} from "components/atoms/UsageOffsetCardData";
import {
  PRODUCT_DISPLAY_ORDER,
  SOLAR_PRODUCT_ID,
} from "constants/bundleAndProductIds";
import { formatCardValueWithSuffix } from "utils/formatCardValue";
import { formatNumber } from "utils/formatHelpers";

type OfferReviewSystemDesignDetailsModalProps = {
  offer: ReviewOffer;
  designImageUrl?: string;
  address?: string;
  onClose: () => void;
};

type SpecRow = {
  label: string;
  value: string;
};

const BATTERY_IDS = new Set<string>(PRODUCT_DISPLAY_ORDER.BATTERIES);

const getSolarPanelCount = (
  products: ReviewOfferProduct[]
): number | undefined => {
  const solar = products.find((p) => p.productId === SOLAR_PRODUCT_ID);
  return solar?.count;
};

const getBatteryCount = (
  products: ReviewOfferProduct[]
): number | undefined => {
  const count = products
    .filter((p) => BATTERY_IDS.has(p.productId))
    .reduce((sum, p) => sum + p.count, 0);
  return count > 0 ? count : undefined;
};

const resolveSystemDesignSpecRows = (offer: ReviewOffer): SpecRow[] => {
  const products = [...offer.includedProducts, ...offer.accessoryProducts];
  const rows: SpecRow[] = [];

  const solarCount = getSolarPanelCount(products);
  if (solarCount !== undefined) {
    rows.push({
      label: "Solar panels",
      value: formatNumber(solarCount),
    });
  }

  const systemSize = systemSizeCardData(offer);
  const systemSizeValue = formatCardValueWithSuffix(systemSize);
  if (systemSizeValue) {
    rows.push({ label: "System size", value: systemSizeValue });
  }

  const production = productionCardData(offer);
  const productionValue = formatCardValueWithSuffix(production);
  if (productionValue) {
    rows.push({
      label: "Est. annual kWh production",
      value: productionValue,
    });
  }

  const usage = currentUsageCardData(offer);
  const usageValue = formatCardValueWithSuffix(usage);
  if (usageValue) {
    rows.push({
      label: "Est. current kWh usage",
      value: usageValue,
    });
  }

  const batteryCount = getBatteryCount(products);
  if (batteryCount !== undefined) {
    rows.push({
      label: "Battery storage",
      value: formatNumber(batteryCount),
    });

    const capacity = storageCapacityCardData(offer);
    const capacityValue = formatCardValueWithSuffix(capacity);
    if (capacityValue) {
      rows.push({
        label: "Storage capacity",
        value: capacityValue,
      });
    }

    const maxPower = maxPowerCardData(offer);
    const maxPowerValue = formatCardValueWithSuffix(maxPower);
    if (maxPowerValue) {
      rows.push({
        label: "Storage max power",
        value: maxPowerValue,
      });
    }
  }

  if (
    offer.annualSelfSupplyPercent !== undefined &&
    offer.annualSelfSupplyPercent !== null
  ) {
    rows.push({
      label: "Est. annual self supply",
      value: `${Number(offer.annualSelfSupplyPercent.toFixed(2))} %`,
    });
  }

  return rows;
};

const OfferReviewSystemDesignDetailsModal = ({
  offer,
  designImageUrl,
  address,
  onClose,
}: OfferReviewSystemDesignDetailsModalProps) => {
  const specRows = resolveSystemDesignSpecRows(offer);
  const title = `${offer.customerName} System Design`;

  return (
    <DesignDetailsModal
      onClose={onClose}
      testId="offer-review-system-design-details-modal"
    >
      <ModalCard>
        <ModalBody>
          <LeftColumn>
            <TitleBlock>
              <Typography size={20} color={tokens.BRAND_HERO_BLUE}>
                {title}
              </Typography>
              {address && (
                <AddressText size={14} color={tokens.BRAND_HERO_BLUE}>
                  {address}
                </AddressText>
              )}
              <Typography size={12} color={tokens.BRAND_HERO_BLUE}>
                Specifications
              </Typography>
            </TitleBlock>
            {designImageUrl && (
              <DesignImage
                src={designImageUrl}
                alt={`${offer.customerName} system design`}
              />
            )}
          </LeftColumn>
          <SpecColumn>
            {specRows.map((row, index) => (
              <SpecRowBlock key={row.label}>
                <SpecRowInner>
                  <SpecLabel size={14} color={tokens.BRAND_HERO_BLUE}>
                    {row.label}
                  </SpecLabel>
                  <SpecValue size={14} color={tokens.BRAND_HERO_BLUE}>
                    {row.value}
                  </SpecValue>
                </SpecRowInner>
                {index < specRows.length - 1 && <SpecDivider />}
              </SpecRowBlock>
            ))}
          </SpecColumn>
        </ModalBody>
      </ModalCard>
    </DesignDetailsModal>
  );
};

const DesignDetailsModal = styled(Modal)`
  > .modal-container {
    width: min(1050px, 94vw);
    > .modal-content-header {
      padding-bottom: 0;
    }
  }
`;

const ModalCard = styled.div`
  position: relative;
  border-radius: 10px;
  background: color-mix(in srgb, ${tokens.WHITE} 30%, transparent);
  backdrop-filter: blur(8px);
  box-sizing: border-box;
  padding-top: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 24px 16px;
  }
`;

const ModalBody = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 409px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    flex: none;
    width: 100%;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 40px;
`;

const AddressText = styled(Typography)`
  && {
    opacity: 0.85;
    line-height: 140%;
  }
`;

const DesignImage = styled.img`
  width: 100%;
  height: auto;
  min-height: 200px;
  max-height: 276px;
  object-fit: cover;
  border-radius: 10px;
  display: block;
`;

const SpecColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding-top: 8px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    width: 100%;
    padding-top: 0;
  }
`;

const SpecRowBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpecRowInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
`;

const SpecLabel = styled(Typography)`
  && {
    font-weight: 500;
    line-height: 140%;
  }
`;

const SpecValue = styled(Typography)`
  && {
    font-weight: 600;
    line-height: 140%;
    text-align: right;
    white-space: nowrap;
  }
`;

const SpecDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${tokens.TINTS_OFF_WHITE_40};
`;

export {
  OfferReviewSystemDesignDetailsModal,
  resolveSystemDesignSpecRows,
  getSolarPanelCount,
  getBatteryCount,
};
